/**
 * Carrega um mapa do catálogo e expõe consultas. O motor não abre arquivo:
 * recebe MapaCarregado pronto. Formato 2 — o v1 é recusado.
 */

import { BLOCO, invalidarMapa } from './blocos';
import {
  carregarImagem, montarTileset, validarNomeAtlas, type TileDef, type Tileset
} from './tileset';
import type { WorldBounds } from './types';
import { carregarNpcs, type NpcNoMapa } from './npcs';
import { carregarPortais, type PortalNoMapa } from './portais';
import { carregarManequins, type ManequimNoMapa } from './manequins';
import { montarRelevo, type RelevoJson, type RelevoMapa } from './relevo';

export { BLOCO } from './blocos';

export interface Estatico {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly tile: number;
  readonly planta?: readonly [number, number];
}

/** Tile de água já recortado no load. `h` é o hash estável do brilho. */
export interface AguaTile {
  readonly tx: number;
  readonly ty: number;
  readonly z: number;
  readonly h: number;
}

const VAZIO_EST: readonly Estatico[] = [];
const VAZIO_AGUA: readonly AguaTile[] = [];

/** Mesmo hash do brilho antigo — o visual não muda, só o laço. */
function hashTile(x: number, y: number): number {
  let h = x * 374761393 + y * 668265263;
  h = (h ^ (h >> 13)) * 1274126177;
  return ((h ^ (h >> 16)) >>> 0) / 4294967296;
}

/**
 * A família `agua` some no empacotado (o Tiled grava string vazia). No
 * cemitério a lagoa nem é terreno: é estático 0x1797… azul. Sem isto o
 * brilho percorre zero tiles.
 */
function pareceAguaCor(r: number, g: number, b: number): boolean {
  return b > 55 && b > r + 12 && b > g + 4 && r < 90;
}

function defsAzuis(imagem: HTMLCanvasElement, defs: readonly TileDef[]): Set<number> {
  const out = new Set<number>();
  const marcado = (d: TileDef) => d.familia === 'agua' || d.nome === 'agua';
  if (typeof document === 'undefined') {
    for (const d of defs) if (marcado(d)) out.add(d.id);
    return out;
  }
  const cv = document.createElement('canvas');
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    for (const d of defs) if (marcado(d)) out.add(d.id);
    return out;
  }
  for (const d of defs) {
    if (marcado(d)) {
      out.add(d.id);
      continue;
    }
    if (d.w < 4 || d.h < 4) continue;
    cv.width = d.w;
    cv.height = d.h;
    ctx.clearRect(0, 0, d.w, d.h);
    ctx.drawImage(imagem, d.x, d.y, d.w, d.h, 0, 0, d.w, d.h);
    let data: ImageData;
    try {
      data = ctx.getImageData(0, 0, d.w, d.h);
    } catch {
      continue;
    }
    let n = 0, r = 0, g = 0, b = 0;
    for (let i = 0; i < data.data.length; i += 16) {
      const a = data.data[i + 3];
      if (a < 16) continue;
      const pr = data.data[i], pg = data.data[i + 1], pb = data.data[i + 2];
      if (pr + pg + pb < 30) continue;
      r += pr; g += pg; b += pb; n++;
    }
    if (n > 8 && pareceAguaCor(r / n, g / n, b / n)) out.add(d.id);
  }
  return out;
}

export interface RegistroMapa {
  readonly id: string;
  readonly homeCity: string;
  readonly arquivo: string;
  readonly nome: string;
  readonly tamanho?: readonly [number, number];
}

export interface MapaCarregado {
  readonly id: string;
  readonly nome: string;
  readonly largura: number;
  readonly altura: number;
  readonly tileW: number;
  readonly tileH: number;
  readonly spawn: { readonly x: number; readonly y: number };
  readonly cerca: WorldBounds;
  readonly origem: { readonly x: number; readonly y: number; readonly facet: string };
  readonly terrenos: Tileset;
  readonly estaticosTs: Tileset;
  readonly chao: readonly Estatico[];
  readonly altos: readonly Estatico[];
  /** União de chão+altos, para quem ainda percorre "todos os estáticos". */
  readonly estaticos: readonly Estatico[];
  readonly regioes: readonly { nome: string; x: number; y: number; w: number; h: number }[];
  readonly npcs: readonly NpcNoMapa[];
  readonly portais: readonly PortalNoMapa[];
  readonly manequins: readonly ManequimNoMapa[];
  /** As fontes de luz do mapa (chão e altos), para a camada de luz da noite. */
  readonly luzes: readonly LuzNoMapa[];
  /**
   * Relevo de verdade (techspec-relevo): terreno esticado e passo 3D. `null` em todo mapa que não traz `relevo` no
   * arquivo, e aí o desenho, o passo e o `zEm` são exatamente os de antes.
   */
  readonly relevo: RelevoMapa | null;

  luzesNoBloco(bx: number, by: number): readonly LuzNoMapa[];
  tileEm(x: number, y: number): number;
  zEm(x: number, y: number): number;
  andavel(x: number, y: number): boolean;
  familiaEm(x: number, y: number): string;
  regiaoEm(x: number, y: number): string;
  dentro(x: number, y: number): boolean;
  altosNoBloco(bx: number, by: number): readonly Estatico[];
  chaoNoBloco(bx: number, by: number): readonly Estatico[];
  aguaNoBloco(bx: number, by: number): readonly AguaTile[];
}

interface TerrenoDefJson {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly andavel?: boolean;
  readonly familia?: string;
  /** Só no mapa com relevo: o texmap esticável [x, y, lado] no atlas. */
  readonly tex?: readonly [number, number, number];
  /** Só no mapa com relevo: desenhar com o texmap mesmo plana (o vazio coberto de neve). */
  readonly texSempre?: boolean;
}

interface EstaticoDefJson {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly ancoraX: number;
  readonly ancoraY: number;
  readonly bloqueia?: boolean;
  readonly andavel?: boolean;
  readonly altura?: number;
  readonly familia?: string;
  readonly luz?: number;
  readonly cor?: string;
  readonly tremula?: boolean;
  readonly animacao?: { readonly quadros?: readonly number[]; readonly passoMs?: number };
}

/** Uma fonte de luz posta no mapa (techspec-dia-e-noite). */
export interface LuzNoMapa {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly luz: number;
  readonly cor: string;
  readonly tremula: boolean;
  /** Altura da arte: a luz nasce na altura da chama, não no pé do item. */
  readonly h: number;
}

interface ArquivoMapa {
  readonly formato: number;
  readonly id: string;
  readonly nome: string;
  readonly largura: number;
  readonly altura: number;
  readonly tileSize: readonly [number, number];
  readonly origem?: { readonly x: number; readonly y: number; readonly facet?: string };
  readonly atlas: string;
  readonly terrenos: readonly TerrenoDefJson[];
  readonly estaticoDefs: readonly EstaticoDefJson[];
  readonly terreno: string;
  readonly z: string;
  readonly chao: string;
  readonly altos: string;
  readonly bloqueado?: string;
  readonly spawn: { readonly x: number; readonly y: number };
  readonly cerca?: WorldBounds;
  readonly regioes?: readonly { nome: string; x: number; y: number; w: number; h: number }[];
  readonly relevo?: RelevoJson;
}

/** Fecha o conjunto: letras/números/hífen, sem ponto nem barra (A05). CamelCase
 *  entra porque o fio já manda `skaraBrae` (JsonNamingPolicy). */
const ID_OK = /^[a-z][a-zA-Z0-9-]*$/;
const ARQ_OK = /^[a-z0-9-]+\.json$/;

/** A animação do arquivo, só se cada quadro aponta para um def que existe (A10: dado ruim não vira laço). */
function animacaoValida(
  a: EstaticoDefJson['animacao'], nDefs: number
): { quadros?: readonly number[]; passoMs?: number } {
  const q = a?.quadros;
  if (!Array.isArray(q) || q.length < 2 || !q.every(n => Number.isInteger(n) && n >= 0 && n < nDefs)) return {};
  const passo = Number(a?.passoMs);
  return { quadros: q, passoMs: Number.isFinite(passo) && passo >= 16 ? passo : 50 };
}

function debase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function chaveCidade(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '');
}

function lerEstaticos(buf: Uint8Array, nDefs: number, rotulo: string): Estatico[] {
  if (buf.length % 5 !== 0)
    throw new Error(`${rotulo}: ${buf.length} bytes não é múltiplo de 5.`);
  const out: Estatico[] = [];
  for (let i = 0; i < buf.length; i += 5) {
    const x = buf[i], y = buf[i + 1], z = buf[i + 2] - 128;
    const tile = buf[i + 3] | (buf[i + 4] << 8);
    if (tile >= nDefs) {
      console.warn(`${rotulo}: def ${tile} fora do atlas; usando 0.`);
      out.push({ x, y, z, tile: 0 });
    } else {
      out.push({ x, y, z, tile });
    }
  }
  return out;
}

let catalogo: readonly RegistroMapa[] | null = null;

async function fetchMapa(url: string, sinal?: AbortSignal): Promise<Response> {
  return fetch(url, { signal: sinal, cache: 'no-cache' });
}

export async function carregarCatalogo(sinal?: AbortSignal): Promise<readonly RegistroMapa[]> {
  if (catalogo) return catalogo;
  const res = await fetchMapa('/mapas/index.json', sinal);
  if (!res.ok) throw new Error(`Catálogo de mapas: ${res.status}`);
  const raw = (await res.json()) as { mapas: RegistroMapa[] };
  if (!Array.isArray(raw.mapas) || raw.mapas.length === 0)
    throw new Error('Catálogo de mapas vazio.');
  for (const r of raw.mapas) {
    if (!ID_OK.test(r.id)) throw new Error(`Catálogo: id de mapa recusado (${r.id}).`);
    if (!ARQ_OK.test(r.arquivo)) throw new Error(`Catálogo: arquivo recusado (${r.arquivo}).`);
  }
  catalogo = raw.mapas;
  return catalogo;
}

export function registroPorCidade(
  lista: readonly RegistroMapa[],
  homeCity: string
): RegistroMapa {
  const chave = chaveCidade(homeCity);
  const achado = lista.find(r =>
    chaveCidade(r.homeCity) === chave
    || chaveCidade(r.id) === chave
    || chaveCidade(r.nome) === chave
  );
  if (!achado)
    throw new Error(`Não há mapa registrado para a cidade "${homeCity}".`);
  return achado;
}

export function registroPorId(lista: readonly RegistroMapa[], id: string): RegistroMapa {
  if (!ID_OK.test(id)) throw new Error(`Id de mapa recusado: "${id}".`);
  const achado = lista.find(r => r.id === id);
  if (!achado) throw new Error(`Não há mapa com id "${id}".`);
  return achado;
}

/** Arquivo próprio — não o stub que ainda aponta para Britain. */
export function mapaPronto(r: RegistroMapa): boolean {
  return r.arquivo.toLowerCase() === `${r.id}.json`.toLowerCase();
}

function montarWalk(
  largura: number,
  altura: number,
  terreno: Uint8Array,
  override: Uint8Array | null,
  terrenos: Tileset,
  estaticos: readonly Estatico[],
  estaticosTs: Tileset
): Uint8Array {
  const walk = new Uint8Array(largura * altura);
  const idx = (x: number, y: number) => y * largura + x;
  for (let y = 0; y < altura; y++) {
    for (let x = 0; x < largura; x++) {
      const def = terrenos.tile(terreno[idx(x, y)]);
      walk[idx(x, y)] = def.andavel === false ? 0 : 1;
    }
  }
  const ordem = estaticos.slice().sort((a, b) => a.z - b.z || a.y - b.y || a.x - b.x);
  for (const e of ordem) {
    if (e.x < 0 || e.y < 0 || e.x >= largura || e.y >= altura) continue;
    const def = estaticosTs.tile(e.tile);
    if (def.andavel) walk[idx(e.x, e.y)] = 1;
    if (def.bloqueia) walk[idx(e.x, e.y)] = 0;
  }
  if (override) {
    for (let i = 0; i < walk.length; i++) {
      if (override[i] === 1) walk[i] = 0;
      else if (override[i] === 2) walk[i] = 1;
    }
  }
  return walk;
}

const residentes = new Map<string, MapaCarregado>();
const ordem: string[] = [];
const RESIDENTES_MAX = 2;

function lembrar(mapa: MapaCarregado): void {
  residentes.set(mapa.id, mapa);
  const i = ordem.indexOf(mapa.id);
  if (i >= 0) ordem.splice(i, 1);
  ordem.push(mapa.id);
  while (ordem.length > RESIDENTES_MAX) {
    const velho = ordem.shift();
    if (!velho || velho === mapa.id) continue;
    residentes.delete(velho);
    invalidarMapa(velho);
  }
}

/** Tira o mapa da RAM para o próximo `carregarMapa` ir à rede de novo. */
export function esquecerMapas(): void {
  for (const id of residentes.keys()) invalidarMapa(id);
  residentes.clear();
  ordem.length = 0;
  catalogo = null;
}

async function lerArquivo(id: string, sinal?: AbortSignal): Promise<MapaCarregado> {
  const lista = await carregarCatalogo(sinal);
  const registro = registroPorId(lista, id);
  if (sinal?.aborted) throw new DOMException('Carga de mapa cancelada.', 'AbortError');
  const res = await fetchMapa(`/mapas/${registro.arquivo}`, sinal);
  if (!res.ok) throw new Error(`Mapa ${registro.arquivo}: ${res.status}`);
  const raw = (await res.json()) as ArquivoMapa;
  if (raw.formato !== 2)
    throw new Error(`Mapa ${registro.arquivo}: formato ${raw.formato} recusado (este leitor só lê o 2).`);

  const atlas = validarNomeAtlas(raw.atlas);
  const imagem = await carregarImagem(`/mapas/${atlas}`, sinal);

  const terrenosDefs: TileDef[] = raw.terrenos.map(t => ({
    id: t.id,
    nome: t.familia ?? `terreno-${t.id}`,
    x: t.x, y: t.y, w: t.w, h: t.h,
    ancoraX: Math.round(t.w / 2), ancoraY: t.h,
    andavel: t.andavel,
    familia: t.familia
  }));
  const estDefs: TileDef[] = raw.estaticoDefs.map(t => ({
    id: t.id,
    nome: `estatico-${t.id}`,
    x: t.x, y: t.y, w: t.w, h: t.h,
    ancoraX: t.ancoraX, ancoraY: t.ancoraY,
    bloqueia: t.bloqueia,
    andavel: t.andavel,
    altura: t.altura,
    familia: t.familia,
    ...(Number.isInteger(t.luz) && (t.luz as number) >= 0
      ? { luz: t.luz, cor: /^#[0-9a-f]{6}$/i.test(t.cor ?? '') ? t.cor : '#ffb45c', tremula: !!t.tremula }
      : {}),
    ...animacaoValida(t.animacao, raw.estaticoDefs.length)
  }));
  const terrenos = montarTileset(`${registro.id}-terreno`, imagem, terrenosDefs);
  const estaticosTs = montarTileset(`${registro.id}-estaticos`, imagem, estDefs);

  const esperado = raw.largura * raw.altura;
  const terreno = debase64(raw.terreno);
  if (terreno.length !== esperado)
    throw new Error(`Mapa ${registro.arquivo}: terreno tem ${terreno.length} bytes, esperado ${esperado}.`);
  const zBuf = debase64(raw.z);
  if (zBuf.length !== esperado)
    throw new Error(`Mapa ${registro.arquivo}: z tem ${zBuf.length} bytes, esperado ${esperado}.`);
  const override = raw.bloqueado ? debase64(raw.bloqueado) : null;
  if (override && override.length !== esperado)
    throw new Error(`Mapa ${registro.arquivo}: bloqueado tem ${override.length} bytes, esperado ${esperado}.`);

  const chao = lerEstaticos(debase64(raw.chao), raw.estaticoDefs.length, `${registro.arquivo} chão`);
  const altos = lerEstaticos(debase64(raw.altos), raw.estaticoDefs.length, `${registro.arquivo} altos`);
  const estaticos = [...chao, ...altos];

  const relevo = raw.relevo
    ? montarRelevo(raw.relevo, raw.largura, raw.altura, zBuf, terreno,
      raw.terrenos.map(t => ({ x: t.x, y: t.y, tex: t.tex, texSempre: t.texSempre === true })), imagem, BLOCO, registro.arquivo)
    : null;
  // No relevo, quem diz onde se fica em pé é o passo 3D do empacotador (o mesmo do servidor), não as flags.
  const walk = relevo
    ? Uint8Array.from({ length: raw.largura * raw.altura }, (_, i) => (relevo.anda(i % raw.largura, Math.floor(i / raw.largura)) ? 1 : 0))
    : montarWalk(raw.largura, raw.altura, terreno, override, terrenos, estaticos, estaticosTs);

  const blocosX = Math.ceil(raw.largura / BLOCO);
  const chaveBloco = (x: number, y: number) =>
    Math.floor(y / BLOCO) * blocosX + Math.floor(x / BLOCO);

  const porBloco = new Map<number, Estatico[]>();
  for (const e of altos) {
    const k = chaveBloco(e.x, e.y);
    const listaB = porBloco.get(k);
    if (listaB) listaB.push(e);
    else porBloco.set(k, [e]);
  }

  const chaoPorBloco = new Map<number, Estatico[]>();
  for (const e of chao) {
    const k = chaveBloco(e.x, e.y);
    const listaC = chaoPorBloco.get(k);
    if (listaC) listaC.push(e);
    else chaoPorBloco.set(k, [e]);
  }
  for (const listaC of chaoPorBloco.values()) {
    listaC.sort((a, b) => (a.x + a.y) - (b.x + b.y) || a.z - b.z || a.x - b.x);
  }

  const idx = (x: number, y: number) => y * raw.largura + x;
  const aguaTerreno = defsAzuis(imagem, terrenosDefs);
  const aguaEstatico = defsAzuis(imagem, estDefs);
  const aguaPorBloco = new Map<number, AguaTile[]>();
  const aguaVisto = new Set<number>();
  const puxarAgua = (x: number, y: number, z: number) => {
    if (x < 0 || y < 0 || x >= raw.largura || y >= raw.altura) return;
    const i = idx(x, y);
    if (aguaVisto.has(i)) return;
    aguaVisto.add(i);
    const k = chaveBloco(x, y);
    const tile: AguaTile = { tx: x, ty: y, z, h: hashTile(x, y) };
    const listaA = aguaPorBloco.get(k);
    if (listaA) listaA.push(tile);
    else aguaPorBloco.set(k, [tile]);
  };
  if (aguaTerreno.size > 0) {
    for (let y = 0; y < raw.altura; y++) {
      for (let x = 0; x < raw.largura; x++) {
        if (!aguaTerreno.has(terreno[idx(x, y)])) continue;
        puxarAgua(x, y, zBuf[idx(x, y)] - 128);
      }
    }
  }
  for (const e of chao) {
    if (aguaEstatico.has(e.tile)) puxarAgua(e.x, e.y, e.z);
  }
  for (const e of altos) {
    if (aguaEstatico.has(e.tile)) puxarAgua(e.x, e.y, e.z);
  }
  const luzes: LuzNoMapa[] = [];
  const luzesPorBloco = new Map<number, LuzNoMapa[]>();
  for (const e of estaticos) {
    const d = estaticosTs.tile(e.tile);
    if (d.luz == null) continue;
    const l: LuzNoMapa = { x: e.x, y: e.y, z: e.z, luz: d.luz, cor: d.cor ?? '#ffb45c', tremula: !!d.tremula, h: d.h };
    luzes.push(l);
    const k = chaveBloco(e.x, e.y);
    const listaL = luzesPorBloco.get(k);
    if (listaL) listaL.push(l);
    else luzesPorBloco.set(k, [l]);
  }
  const dentro = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < raw.largura && y < raw.altura;
  const cerca: WorldBounds = raw.cerca ?? {
    x0: 0, y0: 0, x1: raw.largura - 1, y1: raw.altura - 1
  };
  const npcs = await carregarNpcs(registro.arquivo, sinal);
  const portais = await carregarPortais(registro.arquivo, sinal);
  const manequins = await carregarManequins(registro.arquivo, sinal);

  return {
    id: registro.id,
    nome: registro.nome,
    largura: raw.largura,
    altura: raw.altura,
    tileW: raw.tileSize[0],
    tileH: raw.tileSize[1],
    spawn: raw.spawn,
    cerca,
    origem: {
      x: raw.origem?.x ?? 0,
      y: raw.origem?.y ?? 0,
      facet: raw.origem?.facet ?? 'felucca'
    },
    terrenos,
    estaticosTs,
    chao,
    altos,
    estaticos,
    regioes: raw.regioes ?? [],
    npcs,
    portais,
    manequins,
    luzes,
    relevo,
    luzesNoBloco(bx, by) {
      return luzesPorBloco.get(by * blocosX + bx) ?? [];
    },
    tileEm(x, y) {
      if (!dentro(x, y)) throw new Error(`tileEm fora do mapa: ${x},${y}`);
      return terreno[idx(x, y)];
    },
    zEm(x, y) {
      if (!dentro(x, y)) return 0;
      // No relevo, a altura do pé (o z médio da casa, ou o piso), que é onde o corpo e o clique ficam.
      if (relevo) return relevo.zPe(x, y);
      return zBuf[idx(x, y)] - 128;
    },
    andavel(x, y) {
      return dentro(x, y) && walk[idx(x, y)] === 1;
    },
    familiaEm(x, y) {
      if (!dentro(x, y)) return '';
      return terrenos.tile(terreno[idx(x, y)]).familia ?? '';
    },
    regiaoEm(x, y) {
      for (const r of raw.regioes ?? [])
        if (x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h) return r.nome;
      return `Arredores de ${registro.nome}`;
    },
    dentro,
    altosNoBloco(bx, by) {
      return porBloco.get(by * blocosX + bx) ?? VAZIO_EST;
    },
    chaoNoBloco(bx, by) {
      return chaoPorBloco.get(by * blocosX + bx) ?? VAZIO_EST;
    },
    aguaNoBloco(bx, by) {
      return aguaPorBloco.get(by * blocosX + bx) ?? VAZIO_AGUA;
    }
  };
}

export async function carregarMapa(id: string, sinal?: AbortSignal): Promise<MapaCarregado> {
  const hit = residentes.get(id);
  if (hit) {
    lembrar(hit);
    return hit;
  }
  const mapa = await lerArquivo(id, sinal);
  if (sinal?.aborted) throw new DOMException('Carga de mapa cancelada.', 'AbortError');
  lembrar(mapa);
  return mapa;
}

export async function carregarMapaPorCidade(homeCity: string, sinal?: AbortSignal): Promise<MapaCarregado> {
  const lista = await carregarCatalogo(sinal);
  return carregarMapa(registroPorCidade(lista, homeCity).id, sinal);
}
